import axios from '../../../../app/api-client.ts';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import ButtonIndicator from '../ButtonIndicator.tsx';
import {debounce} from "../../../utils";
import {formatDateTime} from "../../../utils/format.ts";
import {SecureDisputeTimelineItem} from "../../../../types";
import ScrollToBottom from 'react-scroll-to-bottom';

export interface DisputeChatMessageItem {
  id: string;
  isAuthor: boolean;
  actorLabel: string;
  timeLabel: string;
  showHeader: boolean;
  showTimestamp: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  text: string;
  title?: string;
  isEvidence: boolean;
  toneClassName: string;
  externalUrl: string | null;
  fileUrl: string | null;
}

interface DisputeChatInnerProps {
  isLoading: boolean;
  timeline: SecureDisputeTimelineItem[];
  mode: 'message' | 'evidence';
  message: string;
  evidenceTitle: string;
  evidenceComment: string;
  evidenceUrl: string;
  isSending: boolean;
  error: string | null;
  onModeChange: (mode: 'message' | 'evidence') => void;
  onMessageChange: (value: string) => void;
  onEvidenceTitleChange: (value: string) => void;
  onEvidenceCommentChange: (value: string) => void;
  onEvidenceUrlChange: (value: string) => void;
  onEvidenceFileChange: (value: string | string[]) => void;
  onMessageKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  showFileUpload?: boolean;
  view: 'merchant' | 'buyer';
  bodyClassName?: string;
  footerClassName?: string;
  editable?: boolean;
}

function DisputeChatInner({
  isLoading,
  timeline,
  mode,
  message,
  evidenceTitle,
  evidenceComment,
  evidenceUrl,
  isSending,
  error,
  onModeChange,
  onMessageChange,
  onEvidenceTitleChange,
  onEvidenceCommentChange,
  onEvidenceUrlChange,
  onEvidenceFileChange,
  onMessageKeyDown,
  onSubmit,
  showFileUpload = true,
  view,
  bodyClassName,
  footerClassName,
  editable = true,
}: DisputeChatInnerProps) {
  const {t} = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [loadedPreviews, setLoadedPreviews] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledInitiallyRef = useRef(false);
  const previousMessageCountRef = useRef(0);

  const isAuthorByView = (actorType: string) => {
    const normalizedActor = actorType.toLowerCase();
    if (view === 'buyer') return normalizedActor === 'buyer';
    return normalizedActor === 'merchant' || normalizedActor === 'seller';
  };

  const getActorLabel = (actorType: string) =>
    t(`actor_type.${actorType.toUpperCase()}`, {defaultValue: actorType});

  const isBuyerSide = (actorType: string) => actorType.toUpperCase() === 'BUYER';

  const getBubbleTone = (actorType: string) =>
    isBuyerSide(actorType)
      ? 'bg-light-primary border border-primary border-opacity-25'
      : 'bg-light-success border border-success border-opacity-25';

  const messages = useMemo<DisputeChatMessageItem[]>(() => {
    return timeline.map((item, index) => {
      const isEvidence = item.type === 'evidence';
      const isAuthor = isAuthorByView(item.actor_type);

      const prevItem = index > 0 ? timeline[index - 1] : null;
      const nextItem = index < timeline.length - 1 ? timeline[index + 1] : null;
      const sameAuthorAsPrev = prevItem ? isAuthorByView(prevItem.actor_type) === isAuthor : false;
      const sameAuthorAsNext = nextItem ? isAuthorByView(nextItem.actor_type) === isAuthor : false;

      const isFirstInGroup = !sameAuthorAsPrev;
      const isLastInGroup = !sameAuthorAsNext;

      return {
        id: item.id,
        isAuthor,
        actorLabel: getActorLabel(item.actor_type),
        timeLabel: formatDateTime(new Date(item.created_at)),
        showHeader: isFirstInGroup,
        showTimestamp: isLastInGroup,
        isFirstInGroup,
        isLastInGroup,
        text: item.message,
        title: item.title,
        isEvidence,
        toneClassName: getBubbleTone(item.actor_type),
        externalUrl: item.external_url,
        fileUrl: item.file_url,
      } as DisputeChatMessageItem;
    });
  }, [timeline, t, view]);

  const getAttachmentUrl = (item: DisputeChatMessageItem) =>
    item.externalUrl || item.fileUrl || null;

  const isImageAttachment = (url: string) => {
    const clean = url.split('?')[0].toLowerCase();
    return ['.png', '.jpg', '.jpeg', '.webp', '.gif'].some((ext) => clean.endsWith(ext));
  };

  const isPdfAttachment = (url: string) => {
    const clean = url.split('?')[0].toLowerCase();
    return clean.endsWith('.pdf');
  };

  const getAttachmentLabel = (url: string) => {
    try {
      const parsed = new URL(url, window.location.origin);
      const path = parsed.pathname || '';
      const last = path.split('/').filter(Boolean).pop();
      return last || parsed.hostname || t('Attachment', {defaultValue: 'Attachment'});
    } catch {
      return t('Attachment', {defaultValue: 'Attachment'});
    }
  };

  const uploadSelectedFile = async (selectedFile?: File | null) => {
    if (!selectedFile) {
      setSelectedFileName(null);
      setUploadError(null);
      onEvidenceFileChange('');
      return;
    }

    setUploadError(null);
    setSelectedFileName(selectedFile.name);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('path', selectedFile);
      const {data} = await axios.post<{value: string}>(
        '/uploads/dropzone/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      onEvidenceFileChange(String(data.value));
    } catch {
      onEvidenceFileChange('');
      setUploadError(
        t('Unable to upload file. Please try again.', {
          defaultValue: 'Unable to upload file. Please try again.',
        })
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onEvidenceFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await uploadSelectedFile(e.target.files?.[0]);
  };

  const onDropFile = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingFile(false);
    await uploadSelectedFile(e.dataTransfer.files?.[0]);
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      });
    });
  };

  useEffect(() => {
    if (isLoading) return;

    const currentCount = messages.length;
    const previousCount = previousMessageCountRef.current;

    if (currentCount > 0 && !hasScrolledInitiallyRef.current) {
      debounce(() => {
        scrollToBottom('auto');
        hasScrolledInitiallyRef.current = true;
      }, 200)();
    } else if (currentCount > previousCount && previousCount > 0) {
      debounce(() => {
        scrollToBottom('smooth');
      }, 200)();
    }

    previousMessageCountRef.current = currentCount;
  }, [isLoading, messages.length]);

  return (
    <>
      <div className={clsx('', bodyClassName)}>
        {isLoading ? (
          <div className='d-flex h-400px align-items-center justify-content-center py-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>{t('Loading', {defaultValue: 'Loading'})}</span>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className='text-muted h-400px fs-7 text-center py-8'>
            {t('No messages in this dispute yet.', {
              defaultValue: 'No messages in this dispute yet.',
            })}
          </div>
        ) : (
          <ScrollToBottom className={'h-400px me-n5 pe-5 max-h-auto'}>
            {messages.map((item, index) => (
              <div
                key={String(index)}
                className={clsx('d-flex justify-content-start', {
                  'justify-content-end': item.isAuthor,
                  'mb-1': !item.isLastInGroup,
                  'mb-4': item.isLastInGroup,
                  'pe-lg-10': !item.isAuthor,
                  'ps-lg-10': item.isAuthor,
                })}
              >
                <div
                  className={clsx('d-flex flex-column', {
                    'align-items-start': !item.isAuthor,
                    'align-items-end': item.isAuthor,
                  })}
                >
                  {(item.showHeader && !item.isAuthor) && (
                    <div className='d-flex align-items-center mb-1'>
                      {!item.isAuthor && (
                        <div className='symbol symbol-30px symbol-circle me-2'>
                            <span className='symbol-label bg-light-primary text-primary fs-8 fw-bolder'>
                              {item.actorLabel.charAt(0).toUpperCase()}
                            </span>
                        </div>
                      )}
                      <span className='fs-8 fw-bolder text-gray-600'>
                          {item.isAuthor ? t('You', {defaultValue: 'You'}) : item.actorLabel}
                        </span>
                    </div>
                  )}

                  <div
                    className={clsx(
                      'px-3 py-2 rounded text-dark fw-semibold',
                      item.toneClassName,
                      {'text-start': !item.isAuthor, 'text-end': item.isAuthor}
                    )}
                    style={{
                      borderTopLeftRadius:
                        !item.isAuthor && !item.isFirstInGroup ? 8 : 16,
                      borderTopRightRadius:
                        item.isAuthor && !item.isFirstInGroup ? 8 : 16,
                      borderBottomLeftRadius:
                        !item.isAuthor && !item.isLastInGroup ? 8 : 16,
                      borderBottomRightRadius:
                        item.isAuthor && !item.isLastInGroup ? 8 : 16,
                    }}
                  >
                    {item.isEvidence && (
                      <div className='badge badge-light-danger mb-2'>
                        {t('Evidence', {defaultValue: 'Evidence'})}
                      </div>
                    )}
                    <div style={{whiteSpace: 'pre-wrap'}}>{item.text}</div>
                    {(() => {
                      const attachmentUrl = getAttachmentUrl(item);
                      if (!attachmentUrl) return null;

                      const isImage = isImageAttachment(attachmentUrl);
                      const isPdf = isPdfAttachment(attachmentUrl);
                      const isPreviewLoaded = Boolean(loadedPreviews[attachmentUrl]);
                      const attachmentLabel = getAttachmentLabel(attachmentUrl);

                      return (
                        <div className='mt-2 border border-gray-300 rounded bg-white overflow-hidden'>
                          {isImage ? (
                            <div className='position-relative'>
                              {!isPreviewLoaded && (
                                <div
                                  className='placeholder-glow w-100'
                                  style={{height: 180, backgroundColor: '#f1f3f8'}}
                                >
                                  <span className='placeholder col-12 h-100 d-block' />
                                </div>
                              )}
                              <a
                                href={attachmentUrl}
                                target='_blank'
                                rel='noreferrer noopener'
                                className={clsx('d-block', {'d-none': !isPreviewLoaded})}
                              >
                                <img
                                  src={attachmentUrl}
                                  alt={attachmentLabel}
                                  className='w-100'
                                  style={{maxHeight: 180, objectFit: 'cover'}}
                                  onLoad={() =>
                                    setLoadedPreviews((prev) => ({
                                      ...prev,
                                      [attachmentUrl]: true,
                                    }))
                                  }
                                  onError={() =>
                                    setLoadedPreviews((prev) => ({
                                      ...prev,
                                      [attachmentUrl]: true,
                                    }))
                                  }
                                />
                              </a>
                            </div>
                          ) : (
                            <div className='d-flex align-items-center gap-2 px-3 py-2'>
                                <span className='symbol symbol-30px'>
                                  <span className='symbol-label bg-light-primary'>
                                    <i
                                      className={clsx('ki-duotone fs-5 text-primary', {
                                        'ki-document': !isPdf,
                                        'ki-file': isPdf,
                                      })}
                                    />
                                  </span>
                                </span>
                              <div className='d-flex flex-column text-start'>
                                  <span className='text-gray-700 fs-8 fw-semibold text-truncate'>
                                    {item.title ?? attachmentLabel}
                                  </span>
                                {isPdf && (
                                  <span className='text-muted fs-9'>
                                      {t('PDF document', {defaultValue: 'PDF document'})}
                                    </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className='px-3 py-2 border-top border-gray-200 d-flex align-items-center justify-content-between gap-2'>
                            <span className='text-muted fs-9 text-truncate me-2'>{''}</span>
                            <div className='d-flex align-items-center gap-2'>
                              <a
                                href={attachmentUrl}
                                target='_blank'
                                rel='noreferrer noopener'
                                className='btn btn-sm btn-light-primary py-1 px-3'
                              >
                                {t('Open', {defaultValue: 'Open'})}
                              </a>
                              <a
                                href={attachmentUrl}
                                download
                                target='_blank'
                                rel='noreferrer noopener'
                                className='btn btn-sm btn-light py-1 px-3'
                              >
                                {t('Download', {defaultValue: 'Download'})}
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {item.showTimestamp && (
                    <div className='text-muted fs-9 mt-1 px-2'>{item.timeLabel}</div>
                  )}
                </div>
              </div>
            ))}
          </ScrollToBottom>
        )}
      </div>
      {/*<div className={clsx('overflow-y-scroll', bodyClassName)} id='kt_chat_messenger_body'>
        <div
          ref={messagesContainerRef}
          className='scroll-y me-n5 pe-5 h-400px'
          data-kt-element='messages'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_header, #kt_app_header, #kt_app_toolbar, #kt_toolbar, #kt_footer, #kt_app_footer, #kt_chat_messenger_header, #kt_chat_messenger_footer'
          data-kt-scroll-wrappers='#kt_content, #kt_app_content, #kt_chat_messenger_body'
          data-kt-scroll-offset='5px'
        >
          {isLoading ? (
            <div className='d-flex align-items-center justify-content-center py-10'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>{t('Loading', {defaultValue: 'Loading'})}</span>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className='text-muted fs-7 text-center py-8'>
              {t('No messages in this dispute yet.', {
                defaultValue: 'No messages in this dispute yet.',
              })}
            </div>
          ) : (
            messages.map((item, index) => (
              <div
                key={String(index)}
                className={clsx('d-flex justify-content-start', {
                  'justify-content-end': item.isAuthor,
                  'mb-1': !item.isLastInGroup,
                  'mb-4': item.isLastInGroup,
                })}
              >
                <div
                  className={clsx('d-flex flex-column', {
                    'align-items-start': !item.isAuthor,
                    'align-items-end': item.isAuthor,
                  })}
                >
                  {(item.showHeader && !item.isAuthor) && (
                    <div className='d-flex align-items-center mb-1'>
                      {!item.isAuthor && (
                        <div className='symbol symbol-30px symbol-circle me-2'>
                          <span className='symbol-label bg-light-primary text-primary fs-8 fw-bolder'>
                            {item.actorLabel.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <span className='fs-8 fw-bolder text-gray-600'>
                        {item.isAuthor ? t('You', {defaultValue: 'You'}) : item.actorLabel}
                      </span>
                    </div>
                  )}

                  <div
                    className={clsx(
                      'px-3 py-2 rounded text-dark fw-semibold mw-lg-400px',
                      item.toneClassName,
                      {'text-start': !item.isAuthor, 'text-end': item.isAuthor}
                    )}
                    style={{
                      borderTopLeftRadius:
                        !item.isAuthor && !item.isFirstInGroup ? 8 : 16,
                      borderTopRightRadius:
                        item.isAuthor && !item.isFirstInGroup ? 8 : 16,
                      borderBottomLeftRadius:
                        !item.isAuthor && !item.isLastInGroup ? 8 : 16,
                      borderBottomRightRadius:
                        item.isAuthor && !item.isLastInGroup ? 8 : 16,
                    }}
                  >
                    {item.isEvidence && (
                      <div className='badge badge-light-danger mb-2'>
                        {t('Evidence', {defaultValue: 'Evidence'})}
                      </div>
                    )}
                    <div style={{whiteSpace: 'pre-wrap'}}>{item.text}</div>
                    {(() => {
                      const attachmentUrl = getAttachmentUrl(item);
                      if (!attachmentUrl) return null;

                      const isImage = isImageAttachment(attachmentUrl);
                      const isPdf = isPdfAttachment(attachmentUrl);
                      const isPreviewLoaded = Boolean(loadedPreviews[attachmentUrl]);
                      const attachmentLabel = getAttachmentLabel(attachmentUrl);

                      return (
                        <div className='mt-2 border border-gray-300 rounded bg-white overflow-hidden'>
                          {isImage ? (
                            <div className='position-relative'>
                              {!isPreviewLoaded && (
                                <div
                                  className='placeholder-glow w-100'
                                  style={{height: 180, backgroundColor: '#f1f3f8'}}
                                >
                                  <span className='placeholder col-12 h-100 d-block' />
                                </div>
                              )}
                              <a
                                href={attachmentUrl}
                                target='_blank'
                                rel='noreferrer noopener'
                                className={clsx('d-block', {'d-none': !isPreviewLoaded})}
                              >
                                <img
                                  src={attachmentUrl}
                                  alt={attachmentLabel}
                                  className='w-100'
                                  style={{maxHeight: 180, objectFit: 'cover'}}
                                  onLoad={() =>
                                    setLoadedPreviews((prev) => ({
                                      ...prev,
                                      [attachmentUrl]: true,
                                    }))
                                  }
                                  onError={() =>
                                    setLoadedPreviews((prev) => ({
                                      ...prev,
                                      [attachmentUrl]: true,
                                    }))
                                  }
                                />
                              </a>
                            </div>
                          ) : (
                            <div className='d-flex align-items-center gap-2 px-3 py-2'>
                              <span className='symbol symbol-30px'>
                                <span className='symbol-label bg-light-primary'>
                                  <i
                                    className={clsx('ki-duotone fs-5 text-primary', {
                                      'ki-document': !isPdf,
                                      'ki-file': isPdf,
                                    })}
                                  />
                                </span>
                              </span>
                              <div className='d-flex flex-column text-start'>
                                <span className='text-gray-700 fs-8 fw-semibold text-truncate'>
                                  {item.title ?? attachmentLabel}
                                </span>
                                {isPdf && (
                                  <span className='text-muted fs-9'>
                                    {t('PDF document', {defaultValue: 'PDF document'})}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className='px-3 py-2 border-top border-gray-200 d-flex align-items-center justify-content-between gap-2'>
                            <span className='text-muted fs-9 text-truncate me-2'>{''}</span>
                            <div className='d-flex align-items-center gap-2'>
                              <a
                                href={attachmentUrl}
                                target='_blank'
                                rel='noreferrer noopener'
                                className='btn btn-sm btn-light-primary py-1 px-3'
                              >
                                {t('Open', {defaultValue: 'Open'})}
                              </a>
                              <a
                                href={attachmentUrl}
                                download
                                target='_blank'
                                rel='noreferrer noopener'
                                className='btn btn-sm btn-light py-1 px-3'
                              >
                                {t('Download', {defaultValue: 'Download'})}
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {item.showTimestamp && (
                    <div className='text-muted fs-9 mt-1 px-2'>{item.timeLabel}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>*/}

      {editable && (
        <div className={clsx('card-footer pt-4', footerClassName)} id='kt_chat_messenger_footer'>
          <div className='d-flex flex-stack mb-3'>
            <div className='btn-group btn-group-sm' role='group' aria-label='Dispute composer mode'>
              <button
                type='button'
                className={`btn btn-sm ${mode === 'message' ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => onModeChange('message')}
              >
                {t('Message', {defaultValue: 'Message'})}
              </button>
              <button
                type='button'
                className={`btn btn-sm ${mode === 'evidence' ? 'btn-warning' : 'btn-light-warning'}`}
                onClick={() => onModeChange('evidence')}
              >
                {t('Attach evidence', {defaultValue: 'Attach evidence'})}
              </button>
            </div>
          </div>

          {mode === 'message' ? (
            <textarea
              className='form-control form-control-flush mb-3 bg-light-dark'
              rows={3}
              data-kt-element='input'
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              onKeyDown={onMessageKeyDown}
              placeholder={t('Type your message...', {defaultValue: 'Type your message...'})}
            />
          ) : (
            <>
              <input
                type='text'
                className='form-control form-control-solid mb-3'
                value={evidenceTitle}
                onChange={(e) => onEvidenceTitleChange(e.target.value)}
                placeholder={t('Evidence title', {defaultValue: 'Evidence title'})}
              />
              <textarea
                className='form-control form-control-solid mb-3'
                rows={3}
                value={evidenceComment}
                onChange={(e) => onEvidenceCommentChange(e.target.value)}
                placeholder={t('Explain the evidence (optional)...', {defaultValue: 'Explain the evidence...'})}
              />
              <input
                type='url'
                className='form-control form-control-solid mb-3'
                value={evidenceUrl}
                onChange={(e) => onEvidenceUrlChange(e.target.value)}
                placeholder={t('Attachment URL (optional)', {defaultValue: 'Attachment URL (optional)'})}
              />
              {showFileUpload && (
                <div className='mb-3'>
                  {/*<label className='form-label fw-semibold fs-7'>*/}
                  {/*  {t('Attach evidence', {defaultValue: 'Attach evidence'})}*/}
                  {/*</label>*/}
                  <div
                    className={clsx(
                      'form-control form-control-solid d-flex align-items-center justify-content-between px-3 py-2',
                      {'border border-primary': isDraggingFile}
                    )}
                    style={{minHeight: 44, cursor: isUploading ? 'not-allowed' : 'pointer'}}
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDraggingFile(true);
                    }}
                    onDragLeave={() => setIsDraggingFile(false)}
                    onDrop={onDropFile}
                  >
                  <span className='text-muted fs-8'>
                    {selectedFileName
                      ? selectedFileName
                      : t('Drop file or click to browse', {
                        defaultValue: 'Drop file or click to browse',
                      })}
                  </span>
                    <i className='ki-duotone ki-paper-clip fs-4 text-gray-500' />
                  </div>
                  <input
                    ref={fileInputRef}
                    type='file'
                    className='d-none'
                    accept='image/*,application/pdf'
                    onChange={onEvidenceFileSelected}
                    disabled={isUploading}
                  />
                  {isUploading && (
                    <div className='text-muted fs-8 mt-2'>
                      {t('Uploading...', {defaultValue: 'Uploading...'})}
                    </div>
                  )}
                  {selectedFileName && !isUploading && (
                    <div className='text-muted fs-8 mt-2'>
                      {t('Selected file', {defaultValue: 'Selected file'})}: {selectedFileName}
                    </div>
                  )}
                  {uploadError && (
                    <div className='alert alert-danger py-2 mt-2 mb-0'>{uploadError}</div>
                  )}
                </div>
              )}
            </>
          )}

          {error && <div className='alert alert-danger py-2 mb-3'>{error}</div>}

          <div className='d-flex justify-content-end' style={{position: 'absolute', bottom: 10, right: 10}}>
            <ButtonIndicator
              type='button'
              className={mode === 'message' ? 'btn btn-light-primary btn-icon' : 'btn btn-light-warning btn-icon'}
              // label={
              //   mode === 'message'
              //     ? t('Send message', {defaultValue: 'Send message'})
              //     : t('Send evidence', {defaultValue: 'Send evidence'})
              // }
              loading={isSending}
              disabled={isSending}
              onClick={onSubmit}
              marginTop={0}
              width={0}
              icon={'send'}
              style={{borderRadius: '50%'}}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default DisputeChatInner;

